import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, MapPin, CheckCircle2, XCircle, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteLanding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [responding, setResponding] = useState(false);
  const [done, setDone] = useState(null); // "accepted" | "declined"
  const [mode, setMode] = useState("quick"); // "quick" | "full"
  const [extra, setExtra] = useState({ phone: "", company: "" });
  const [full, setFull] = useState({
    full_name: "", email: "", phone: "", company: "", role: "",
    dietary: "", bring_guest: false, guest_name: "", message: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/invites/${token}`);
        setData(r.data);
        setFull((f) => ({ ...f, full_name: r.data.guest_name || "", email: r.data.guest_email || "" }));
        if (r.data.status === "accepted") setDone("accepted");
        if (r.data.status === "declined") setDone("declined");
      } catch (e) {
        setError(e?.response?.data?.detail || "Convite inválido ou expirado.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const respondQuick = async (response) => {
    setResponding(true);
    try {
      await axios.post(`${API}/invites/${token}/respond`, {
        response,
        phone: extra.phone || undefined,
        company: extra.company || undefined,
      });
      setDone(response);
      toast.success(response === "accepted" ? "Confirmação registada. Até breve!" : "Resposta registada. Obrigado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registar resposta.");
    } finally { setResponding(false); }
  };

  const submitFull = async (e) => {
    e.preventDefault();
    setResponding(true);
    try {
      await axios.post(`${API}/invites/${token}/rsvp`, full);
      setDone("accepted");
      toast.success("Inscrição completa registada. Até breve!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao submeter inscrição.");
    } finally { setResponding(false); }
  };

  if (loading) {
    return (
      <PublicShell>
        <p className="text-[hsl(var(--muted-foreground))]">A carregar convite…</p>
      </PublicShell>
    );
  }

  if (error || !data) {
    return (
      <PublicShell>
        <div className="overline text-red-700">Convite inválido</div>
        <h1 className="display-heading text-3xl mt-3">Este convite não está disponível.</h1>
        <p className="mt-4 text-[hsl(var(--muted-foreground))]">{error || "Verifique se o link está completo."}</p>
        <Link to="/" className="mt-6 inline-block text-[hsl(var(--primary))] underline">Ir para ciees.pt</Link>
      </PublicShell>
    );
  }

  const ev = data.event;
  const firstName = (data.guest_name || "").split(" ")[0] || "";

  // SUCCESS/DECLINED state
  if (done === "accepted") {
    return (
      <PublicShell>
        <div className="text-center max-w-xl mx-auto py-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 border border-emerald-200 mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-700" />
          </div>
          <div className="overline text-emerald-700">Confirmado</div>
          <h1 className="display-heading text-3xl sm:text-4xl mt-3">Até breve, {firstName}!</h1>
          <p className="mt-5 text-[hsl(var(--muted-foreground))] leading-relaxed">
            A sua presença em <strong>{ev.title}</strong> está registada.
            Vamos enviar-lhe os detalhes finais por email. <strong>{data.host_name}</strong> também
            foi avisado de que vai estar lá.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-3 text-sm text-left bg-[hsl(var(--secondary))] p-5">
            <DetailRow icon={Calendar} label="Data" value={ev.date} />
            <DetailRow icon={MapPin} label="Local" value={ev.location} />
          </div>
          <Link to="/" className="mt-8 inline-block text-[hsl(var(--primary))] underline text-sm">
            Saber mais sobre o CIEES Portugal
          </Link>
        </div>
      </PublicShell>
    );
  }

  if (done === "declined") {
    return (
      <PublicShell>
        <div className="text-center max-w-xl mx-auto py-8">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] mb-6">
            <XCircle className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />
          </div>
          <div className="overline">Resposta registada</div>
          <h1 className="display-heading text-3xl mt-3">Obrigado pela resposta.</h1>
          <p className="mt-5 text-[hsl(var(--muted-foreground))]">
            Compreendemos que desta vez não poderá comparecer. {data.host_name} foi notificado.
            Ficamos disponíveis para próximas oportunidades.
          </p>
          <Link to="/" className="mt-8 inline-block text-[hsl(var(--primary))] underline text-sm">
            Saber mais sobre o CIEES Portugal
          </Link>
        </div>
      </PublicShell>
    );
  }

  // PENDING — show invite + actions
  return (
    <PublicShell>
      <div className="max-w-2xl mx-auto">
        <div className="overline flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Convite pessoal</div>
        <h1 className="display-heading text-3xl sm:text-4xl mt-3" data-testid="invite-greeting">
          {firstName ? `Olá ${firstName},` : "Olá,"}
        </h1>
        <p className="mt-4 text-[hsl(var(--foreground))] text-lg leading-relaxed">
          <strong>{data.host_name}</strong> convidou-o pessoalmente para o evento do
          CIEES Portugal:
        </p>

        {/* Event card */}
        <div className="mt-6 bg-white border border-[hsl(var(--border))]">
          {ev.image_url && (
            <img src={ev.image_url} alt={ev.title} className="w-full h-48 object-cover" />
          )}
          <div className="p-6 lg:p-8">
            <h2 className="display-heading text-2xl text-[hsl(var(--primary))]" data-testid="invite-event-title">{ev.title}</h2>
            {ev.subtitle && <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">{ev.subtitle}</p>}
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[hsl(var(--foreground))]">
              {ev.date && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> {ev.date}</span>}
              {ev.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> {ev.location}</span>}
            </div>
            {data.message && (
              <div className="mt-6 border-l-2 border-[hsl(var(--accent))] pl-4 py-2 bg-[hsl(var(--secondary))]/50">
                <div className="overline text-[10px] mb-1">Mensagem pessoal de {data.host_name}</div>
                <p className="text-sm leading-relaxed text-[hsl(var(--foreground))] whitespace-pre-line">{data.message}</p>
              </div>
            )}
            {ev.body && (
              <p className="mt-6 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed line-clamp-6 whitespace-pre-line">
                {ev.body.slice(0, 400)}{ev.body.length > 400 ? "…" : ""}
              </p>
            )}
          </div>
        </div>

        {/* Response actions */}
        {mode === "quick" ? (
          <div className="mt-8" data-testid="invite-actions">
            <div className="overline">A sua resposta</div>
            <h3 className="display-heading text-xl mt-2">Posso contar consigo?</h3>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={() => respondQuick("accepted")} disabled={responding}
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[hsl(var(--primary))]/90 disabled:opacity-50"
                data-testid="invite-quick-accept"
              >
                <CheckCircle2 className="h-4 w-4" /> Sim, vou estar lá
              </button>
              <button
                onClick={() => respondQuick("declined")} disabled={responding}
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-[hsl(var(--border))] text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--secondary))] disabled:opacity-50"
                data-testid="invite-quick-decline"
              >
                <XCircle className="h-4 w-4" /> Não posso desta vez
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMode("full")}
              className="mt-5 text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] underline"
              data-testid="invite-mode-full"
            >
              Preencher inscrição completa →
            </button>
          </div>
        ) : (
          <form onSubmit={submitFull} className="mt-8 bg-white border border-[hsl(var(--border))] p-6 lg:p-8" data-testid="invite-full-form">
            <button type="button" onClick={() => setMode("quick")} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] mb-4">← Voltar à confirmação rápida</button>
            <div className="overline">Inscrição completa</div>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <FullField label="Nome completo *" value={full.full_name} onChange={(v) => setFull({ ...full, full_name: v })} required testid="full-name" />
              <FullField label="Email *" type="email" value={full.email} onChange={(v) => setFull({ ...full, email: v })} required testid="full-email" />
              <FullField label="Telefone" value={full.phone} onChange={(v) => setFull({ ...full, phone: v })} testid="full-phone" />
              <FullField label="Empresa" value={full.company} onChange={(v) => setFull({ ...full, company: v })} testid="full-company" />
              <FullField label="Cargo / Função" value={full.role} onChange={(v) => setFull({ ...full, role: v })} testid="full-role" />
              {ev?.has_meal && (
                <FullField label="Restrições alimentares" value={full.dietary} onChange={(v) => setFull({ ...full, dietary: v })} testid="full-dietary" />
              )}
            </div>
            <div className="mt-5">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={full.bring_guest} onChange={(e) => setFull({ ...full, bring_guest: e.target.checked })} />
                Vou levar acompanhante
              </label>
              {full.bring_guest && (
                <div className="mt-3">
                  <FullField label="Nome do acompanhante" value={full.guest_name} onChange={(v) => setFull({ ...full, guest_name: v })} testid="full-guest-name" />
                </div>
              )}
            </div>
            <div className="mt-5">
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Mensagem (opcional)</label>
              <textarea
                value={full.message} onChange={(e) => setFull({ ...full, message: e.target.value })}
                rows={3}
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="full-message"
              />
            </div>
            <button
              type="submit" disabled={responding}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[hsl(var(--primary))]/90 disabled:opacity-50"
              data-testid="invite-full-submit"
            >
              <CheckCircle2 className="h-4 w-4" /> {responding ? "A submeter…" : "Confirmar inscrição"}
            </button>
          </form>
        )}

        <p className="mt-12 text-[11px] text-[hsl(var(--muted-foreground))] text-center">
          O CIEES Portugal é a comunidade portuguesa de empresários e executivos schoenstattianos.
          <br/>
          <Link to="/" className="underline">Saber mais</Link>
        </p>
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <section className="container-page py-12 lg:py-16 min-h-[60vh]">
      {children}
    </section>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
    </div>
  );
}

function FullField({ label, value, onChange, testid, type = "text", required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={!!required}
        className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
        data-testid={testid}
      />
    </div>
  );
}
